import { APP_INITIALIZER, enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { environment } from '@env-team/environment';
import { provideStore, Store } from '@ngrx/store';
import { teamRegisterStore } from '@team/pages/register/store/team-register.reducer';
import { provideEffects } from '@ngrx/effects';
import { TeamRegisterEffects } from '@team/pages/register/store/team-register.effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { IonicStorageModule } from '@ionic/storage-angular';
import { Drivers } from '@ionic/storage';
import { appStore } from '@core/store/core-app.reducer';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import {
  DefaultDataServiceConfig,
  EntityDataModule,
  EntityMetadataMap,
  provideEntityData,
} from '@ngrx/data';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TeamInfoApiEffects } from '@team/pages/infos/store/team-info-api.effects';
import { teamInfoStore } from '@team/pages/infos/store/team-info.reducers';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { attachmentsStore } from '@shared/components/attachments/store/attachments.reducers';
import { AttachmentsApiEffects } from '@shared/components/attachments/store/attachments-api.effects';
import { teamSideBarStore } from '@team/components/side-bar/store/team-side-bar.reducers';
import { TeamSideBarApiEffects } from '@team/components/side-bar/store/team-side-bar-api.effects';
import * as routes from '@team/routes';
import { TeamAppComponent } from '@team/team-app.component';
import { teamIssueStore } from '@team/pages/issues/store/team-issue.reducers';
import CordovaSQLiteDriver from 'localforage-cordovasqlitedriver';
import {defineCustomElements} from '@ionic/pwa-elements/loader';
import {TeamIssueApiEffects} from "@team/pages/issues/store/team-issue-api.effects";
import {sharedDocumentStore} from "@shared/components/documents/store/shared-document.reducers";
import {SharedDocumentApiEffects} from "@shared/components/documents/store/shared-document.api-effects";
import { sharedKnowledgeStore } from '@shared/components/knowledge/store/knowledge.reducers';
import { SharedKnowledgeApiEffects } from '@shared/components/knowledge/store/knowledge-api.effects';
import {CoreCustomTranslateHttpLoaderService} from "@core/services/core-custom-translate-http-loader.service";
import {SharedPermissionsEffects} from "@shared/stores/permissions/store/shared-permissions.effects";
import {sharedPermissionsStore} from "@shared/stores/permissions/store/shared-permissions.reducer";
import {sharedChatStore} from "@shared/components/chat/store/shared-chat.reducers";
import {SharedChatApiEffects} from "@shared/components/chat/store/shared-chat.api-effects";
import {sharedNotificationListStore} from "@shared/components/notification-list/store/shared-notification-list.reducers";
import {SharedNotificationListApiEffects} from "@shared/components/notification-list/store/shared-notification-list-api.effects";
import {sharedLoginStore} from "@shared/components/login/store/shared-login.reducer";
import {SharedLoginEffects} from "@shared/components/login/store/shared-login.effects";
import {registerLocaleData} from "@angular/common";
import localeDe from '@angular/common/locales/de';
import {SharedLoginActions} from "@shared/components/login/store/shared-login.actions-type";

// currencyPipe it is only include support to en-US
// so we need to register the local de-DE to Angular to allow currencyPipe work also with it
registerLocaleData(localeDe);

if (environment.production) {
  enableProdMode();
}

defineCustomElements(window);

const entityMetadataMap: EntityMetadataMap = {
  Chat: {
    selectId: chat => chat.chatByFormDefinitionId
  }
};

const defaultDataServiceConfig: DefaultDataServiceConfig = {
  root: environment.apiUrl.one
}

export function createTranslateLoader(http: HttpClient): CoreCustomTranslateHttpLoaderService {
  return new CoreCustomTranslateHttpLoaderService(http);
}

export function setBrandInitializer(store: Store): () => void {
  return () => store.dispatch(SharedLoginActions.setBrand({ brand: environment.brand }));
}

bootstrapApplication(TeamAppComponent, {
  providers: [
    provideZoneChangeDetection(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: DefaultDataServiceConfig, useValue: defaultDataServiceConfig },
    provideStore({
      app: appStore,
      teamInfos: teamInfoStore,
      teamRegister: teamRegisterStore,
      sharedKnowledge: sharedKnowledgeStore,
      sharedDocuments: sharedDocumentStore,
      sharedChat: sharedChatStore,
      sharedLogin: sharedLoginStore,
      sharedPermissions: sharedPermissionsStore,
      issues: teamIssueStore,
      attachments: attachmentsStore,
      sideBar: teamSideBarStore,
      sharedNotifications: sharedNotificationListStore
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: setBrandInitializer,
      deps: [Store],
      multi: true
    },
    provideEffects([
      TeamRegisterEffects,
      TeamInfoApiEffects,
      SharedKnowledgeApiEffects,
      SharedDocumentApiEffects,
      SharedChatApiEffects,
      SharedLoginEffects,
      TeamIssueApiEffects,
      AttachmentsApiEffects,
      TeamSideBarApiEffects,
      SharedPermissionsEffects,
      SharedNotificationListApiEffects
    ]),
    provideStoreDevtools({
      name: 'semco ONE'
    }),
    provideRouter(routes.default),
    provideEntityData({ entityMetadata: entityMetadataMap }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    importProvidersFrom(
      IonicModule.forRoot({
        spinner: 'dots',
        swipeBackEnabled: false,
      }),
      IonicStorageModule.forRoot({
        name: 'one',
        driverOrder: [
          CordovaSQLiteDriver._driver,
          Drivers.IndexedDB,
          Drivers.LocalStorage
        ]
      }),
      HttpClientModule,
      BrowserAnimationsModule,
      EntityDataModule.forRoot({})
    ),
  ]
});
